import React, { useCallback, useEffect, useRef, useMemo } from 'react'
import { OneNotification } from '@yy/one-ui'
import Cookies from 'js-cookie'

import socket from '@/services/socket'
import store, { useSelector, useDispatch } from '@/store'
import { openCtr, setUnReadTaskCount } from '@/store/taskSlice'

import { setUnReadMessageCount, setNotifyCount } from '@/store/messageSlice'
import { useTranslation } from 'react-i18next'
import { StatusEnum } from '@/containers/TaskCenter/utils'
import { getTaskDetail } from '@/services/api'
import { jsonParse } from '@/utils'
import { NOTICE_SETTING_LIST_MAP } from '@/containers/Setting/Notice/LogisticStatusNotice/constant'
import { MERGE_ORDER_TRACK_STAGE, useMergeOrderTrack } from '@/containers/Order/PreMerge/hook/useMergeOrderTrack'
import type { MergerOrderTaskInfo } from '@/containers/TaskCenter/components/TaskItem/MergeOrder/type'
import { REF_OBJECT } from '@/constants/reference'
import { MERGE_TYPE } from '@/containers/Order/PreMerge/constant'
import { Link } from '@yy/one-icon'
import { useMemoizedFn } from './useMemoizedFn'
import { useCaptcha, wrapCaptcha } from './useCaptcha'
import { CAPTCHA_SCENE, FINGER_PRINT_PUBLIC_KEY_META } from '@/constants/publicKey'
import { omit } from 'lodash'

/** socket 配置与初始化 */
export const useSocket = () => {
  const notifyCount = useSelector((state) => state.message.notifyCount)
  const unReadMsgCount = useSelector((state) => state.message.unReadMessageCount)
  const isInit = useRef(false)
  const { t } = useTranslation()
  const { mergerOrderResultTrack, mergeOrderTrack } = useMergeOrderTrack()
  const dispatch = useDispatch()
  const { validateDownloadUrl } = useCaptcha()

  const ACTION_MAP = useMemo(() => {
    return {
      /**导出 */
      export: t('task.export.tipType.export'),
      /**拦截 */
      intercept: t('task.export.tipType.orderIntercept'),
      /**导入 */
      import: t('task.export.tipType.import'),
      /**同步 */
      pull: t('task.export.tipType.pull'),
      /**出货 */
      shipment: t('task.export.tipType.orderShipment'),
      /**恢复 */
      recover: t('task.export.tipType.orderRecover'),
      /**更新 */
      update: t('task.export.tipType.platformOrderUpdate'),
      /**修改物流 */
      updateExpress: t('task.export.tipType.action', { action: t('order.index.changeLogisticOrWarehouse') }),
      /** 拆单 */
      split: t('task.export.tipType.split'),
      /** 取消拆单 */
      cancelSplit: t('task.export.tipType.cancelSplit'),
      /** 库存同步 */
      stockSync: `【${t('setting.stock.title')}】`,
      /** 下载面单 */
      orderWaybillDownload: t('task.download.name', { taskType: t('order.faceSheet.title') }),
      /** 编辑 */
      edit: t('task.editType'),
    }
  }, [t])

  const renderMessage = useCallback((des: string | (() => React.ReactNode)) => {
    if (typeof des === 'function') {
      return des()
    }
    return des
  }, [])

  /**直接下载 */
  const handleDownload = useMemoizedFn(({ token, id, url: fileUrl }: { token: string; id: string; url: string }) => {
    return validateDownloadUrl({
      url: fileUrl,
      token,
      taskId: id,
      captchaScene: CAPTCHA_SCENE.DOWNLOAD_CENTER,
    }).then(({ url }) => {
      url && window.open(url)
    })
  })

  /**打开任务中心 */
  const handleGoTaskCenter = useMemoizedFn((e) => {
    e.nativeEvent.stopImmediatePropagation()
    dispatch(openCtr(true))
  })

  /** 未读通知消息 */
  const queryUnRead = useCallback(
    async (id: string) => {
      // ow已经不是标准的【任务类型】+任务名称了
      const TASK_TYPE = {
        order_export: ACTION_MAP.export,
        product_export: ACTION_MAP.export,
        product_group_export: ACTION_MAP.export,
        stock_export: ACTION_MAP.export,
        inbound_order_export: ACTION_MAP.export,
        stock_flow_export: ACTION_MAP.export,
        order_intercept: ACTION_MAP.intercept,
        product_import: ACTION_MAP.import,
        product_group_import: ACTION_MAP.import,
        order_pull: ACTION_MAP.pull,
        sku_pull: ACTION_MAP.pull,
        order_import: ACTION_MAP.import,
        order_shipment: ACTION_MAP.shipment,
        platform_order_update: ACTION_MAP.update,
        order_update_express: ACTION_MAP.updateExpress,
        order_recover: ACTION_MAP.recover,
        inbound_order_import: ACTION_MAP.import,
        order_split: ACTION_MAP.split,
        order_split_cancel: ACTION_MAP.cancelSplit,
        refund_order_export: ACTION_MAP.export,
        inbound_trend_export: ACTION_MAP.export,
        stock_turnover_export: ACTION_MAP.export,
        inbound_analysis_export: ACTION_MAP.export,
        outbound_trend_export: ACTION_MAP.export,
        stock_statistics_export: ACTION_MAP.export,
        sales_analysis_export: ACTION_MAP.export,
        logistics_analysis_export: ACTION_MAP.export,
        product_statistics_export: ACTION_MAP.export,
        product_bestsell_export: ACTION_MAP.export,
        product_unsalable_export: ACTION_MAP.export,
        approach_statistics_export: ACTION_MAP.export,
        product_lack_export: ACTION_MAP.export,
        stock_flow_page_export: ACTION_MAP.export,
        subscription_bill_export: ACTION_MAP.export,
        subscription_sms_export: ACTION_MAP.export,
        stock_sync: ACTION_MAP.stockSync,
        order_allocate: `【${t('order.allocated.operation')}】`,
        order_cancel_appointment: `【${t('order.bookShip.cancel')}】`,
        order_waybill_download: ACTION_MAP.orderWaybillDownload,
        order_invoice_download: `【${t('order.list.downLoadBill')}】`,
        notify_template_auth_result: () => {
          return t('email.setting.notice.title', {
            logisticsStatus: t(NOTICE_SETTING_LIST_MAP[taskData.logisticsStatus]),
          })
        },
        order_batch_add_sku: t('task.export.tipType.action', { action: t('order.goods.add') }),
        order_batch_update_sku: t('task.export.tipType.action', { action: t('order.goods.update') }),
        order_batch_delete_sku: t('task.export.tipType.action', { action: t('order.goods.delete') }),
        refund_order_update_warehouse: `【${t('task.refund.updateWarehouse.action')}】`,
        outbound_custom_label_update: t('task.msg.title'),
        download_inbound_label: t('task.download'),
      }
      const TASK_NAME = {
        order_export: t('task.export.tipName.type', { taskType: t('export.taskType.order') }),
        product_export: t('task.export.tipName.type', { taskType: t('export.taskType.goods') }),
        stock_export: t('task.export.tipName.type', { taskType: t('export.taskType.stock') }),
        inbound_order_export: t('task.export.tipName.type', { taskType: t('export.taskType.inbound') }),
        stock_flow_export: t('task.export.tipName.type', { taskType: t('export.taskType.stockDetails') }),
        stock_flow_page_export: t('task.export.tipName.type', { taskType: t('export.taskType.stockDetails') }),
        order_intercept: t('task.export.tipName.orderIntercept'),
        product_import: t('task.export.tipName.productImport'),
        order_pull: t('task.export.tipName.orderPull'),
        sku_pull: t('task.export.tipName.skuPull'),
        order_import: t('task.export.tipName.orderImport'),
        order_shipment: t('task.export.tipName.orderShipment'),
        platform_order_update: t('task.export.tipName.platformOrderUpdate'),
        order_update_express: t('normal.action.batch', { action: t('order.index.changeLogisticOrWarehouse') }),
        order_recover: t('task.export.tipName.orderRecover'),
        inbound_order_import: t('task.export.tipName.inboundOrderImport'),
        order_split: t('message.split.batchSplit'),
        order_split_cancel: t('task.taskType.order_split_cancel'),
        inbound_trend_export: t('task.export.tipName.type', { taskType: t('dataAnalysis.storageStatistics.details') }),
        stock_turnover_export: t('task.export.tipName.type', { taskType: t('dataAnalysis.inventoryTurnover.details') }),
        inbound_analysis_export: t('task.export.tipName.type', { taskType: t('dataAnalysis.storage.detail') }),
        outbound_trend_export: t('task.export.tipName.type', { taskType: t('dataAnalysis.outboundStatistics.details') }),
        stock_statistics_export: t('task.export.tipName.type', { taskType: t('dataAnalysis.inventoryStatistics.details') }),
        sales_analysis_export: t('task.export.tipName.type', {
          taskType: t('dataAnalysis.saleAnalysis.title'),
        }),
        subscription_bill_export: t('task.export.tipName.type', {
          taskType: t('subscribe.bill.record'),
        }),
        subscription_sms_export: t('task.export.tipName.type', {
          taskType: t('subscribe.sms.record'),
        }),
        logistics_analysis_export: t('task.export.tipName.type', {
          taskType: t('common.detailWithType', {
            type: t('common.logisticChannel'),
          }),
        }),
        product_statistics_export: t('task.export.tipName.type', {
          taskType: t('dataAnalysis.goodStatistic.title'),
        }),
        product_bestsell_export: t('task.export.tipName.type', {
          taskType: t('enum.hotSale'),
        }),
        product_unsalable_export: t('task.export.tipName.type', {
          taskType: t('enum.unsalable'),
        }),
        approach_statistics_export: t('task.export.tipName.type', {
          taskType: t('enum.expiring'),
        }),
        product_lack_export: t('task.export.tipName.type', {
          taskType: t('enum.outOfStock'),
        }),
        product_status: t('goods.batch.disable.goods'),
        sku_delete: t('goods.batchModify.deleteSku'),
        stock_sync: t('setting.stock.title'),
        order_allocate: t('order.allocated.operation'),
        order_cancel_appointment: t('order.bookShip.cancel'),
        order_waybill_download: t('task.taskType.face_sheet_download'),
        order_invoice_download: t('order.list.downLoadBill'),
        notify_template_auth_result: () => {
          return t('email.setting.notice.title', {
            logisticsStatus: t(NOTICE_SETTING_LIST_MAP[taskData.logisticsStatus]),
          })
        },
        order_batch_add_sku: t('normal.action.batch', { action: t('order.goods.add') }),
        order_batch_update_sku: t('normal.action.batch', { action: t('order.goods.update') }),
        order_batch_delete_sku: t('normal.action.batch', { action: t('order.goods.delete') }),
        // refund_order_update_warehouse: t('task.refund.updateWarehouse.action')
        download_inbound_label: t('download_inbound_label.title'),
      }
      const response = await getTaskDetail({
        batchTaskId: id,
      })

      const { taskType, taskStatus, taskData } = response?.data || {}

      // 成功通知的自定义描述
      const CUSTOM_TASK_SUCCESS_DES: Record<string, string | (() => string)> = {
        order_split: t('message.split.batchSplitTips'),
        /** 同步商品 */
        sku_pull: () => {
          const { succeedCount, failedCount } = taskData
          const hasSuccessGoods = succeedCount > 0
          const hasFailGoods = failedCount > 0
          if (hasSuccessGoods && hasFailGoods) {
            return t('task.skuSync.tip', { count: succeedCount }) + ',' + t('task.skuSync.error', { skuNum: failedCount })
          }
          if (!hasSuccessGoods && hasFailGoods) {
            return t('task.skuSync.noFound') + ',' + t('task.skuSync.error', { skuNum: failedCount })
          }
        },
        refund_order_update_warehouse: () => {
          const { successCount, totalCount } = taskData
          return t('task.refundUpdateWarehouse.suc.content', { successCount, totalCount })
        },
        outbound_custom_label_update: () => t('task.uploadLabel.success', { successCount: taskData.successCount, totalCount: taskData.totalCount }),
        download_inbound_label: () => t('download_inbound_label.notification.success'),
      }

      /**批量修改商品的报错信息 */
      const getBtachChangeSkuDes = (action: string) => {
        return () => {
          const { totalCount, failCount } = taskData
          return (
            <>
              {`${t('taskcenter.messageFailSuccTip', { totalCount, failedCount: failCount, taskType: t('normal.action.batch', { action: t(action) }) })}`}
              <a onClick={handleGoTaskCenter}>{t('common.goTaskCenter')}</a>
            </>
          )
        }
      }

      // 失败通知的自定义描述
      const CUSTOM_TASK_ERROR_DES: Record<string, string | (() => string | React.ReactNode)> = {
        order_batch_delete_sku: getBtachChangeSkuDes('order.goods.delete'),
        order_batch_update_sku: getBtachChangeSkuDes('order.goods.update'),
        order_batch_add_sku: getBtachChangeSkuDes('order.goods.add'),
        refund_order_update_warehouse: () => {
          const { failCount, totalCount } = taskData
          return t('task.refundUpdateWarehouse.fail.content', { failCount, totalCount })
        },
        outbound_custom_label_update: () => {
          const { failCount, totalCount, successCount } = taskData
          const des: any = [
            successCount
              ? t('task.uploadLabel.finish.success.fail', { failCount, totalCount, successCount })
              : t('task.uploadLabel.finish.fail', { failCount: failCount, totalCount: totalCount }),
          ]
          des.push(<a onClick={handleGoTaskCenter}>{t('common.goTaskCenter')}</a>)
          return des
        },
        download_inbound_label: () => t('download_inbound_label.notification.fail'),
      }

      if (taskStatus === StatusEnum.SUCCESS) {
        const { fileUrl } = taskData
        const CaptchaButton = wrapCaptcha((props) => {
          const withChildrenProps = omit(props, ['children', 'loading'])
          return (
            <div style={{ color: '#356DFF', display: 'flex', alignItems: 'center', cursor: 'pointer' }} {...withChildrenProps}>
              {t('task.download')}
              <Link style={{ fontSize: 16, marginLeft: 2 }} />
            </div>
          )
        })
        OneNotification.success({
          message: t('task.export.tip.title.success', { type: renderMessage(TASK_TYPE[taskType]) }),
          description: (
            <>
              {CUSTOM_TASK_SUCCESS_DES[taskType] ? renderMessage(CUSTOM_TASK_SUCCESS_DES[taskType]) : t('task.export.tip.content.success', { name: TASK_NAME[taskType] })}
              {fileUrl && (
                <CaptchaButton
                  publicKey={FINGER_PRINT_PUBLIC_KEY_META.DOWNLOAD_CENTER}
                  onCaptchaClick={async (e, token) => {
                    return handleDownload({ token, id, url: fileUrl })
                  }}
                />
              )}
            </>
          ),
        })
      } else if ([StatusEnum.ALL_FAILURE, StatusEnum.PARTIAL_FAILURE].includes(taskStatus)) {
        OneNotification.error({
          message: t('task.export.tip.title.error', { type: renderMessage(TASK_TYPE[taskType]) }),
          description: (
            <>
              {CUSTOM_TASK_ERROR_DES[taskType] ? renderMessage(CUSTOM_TASK_ERROR_DES[taskType]) : t('task.export.tip.content.error', { name: renderMessage(TASK_NAME[taskType]) })}
            </>
          ),
        })
      }

      // 合单结果上报
      if (taskType === 'order_merge') {
        const taskInfo = (taskData || REF_OBJECT) as MergerOrderTaskInfo['taskData']
        mergerOrderResultTrack({
          aa_merge_order_success_count: taskInfo.orderSuccessCount,
          aa_merge_order_total: taskInfo.orderTotalCount,
          aa_merge_order_fail_count: taskInfo.failCount,
          aa_merge_order_merge_batch_id: taskInfo.mergeBatchId,
          aa_merge_order_merge_type: taskInfo.mergeType,
          aa_merge_order_total_fail_count: taskInfo.orderFailCount,
          aa_task_type: taskType,
        })
      }

      // 点击智能合单-上报预合单信息
      if (taskType === 'order_auto_pre_merge') {
        const taskInfo = (taskData || REF_OBJECT) as MergerOrderTaskInfo['taskData']
        // 手动合单埋点上报
        mergeOrderTrack({
          aa_merge_order_operation: MERGE_ORDER_TRACK_STAGE.autoCreate,
          aa_merge_order_merge_type: MERGE_TYPE.auto,
          aa_merge_order_merge_batch_id: taskInfo?.mergeBatchId,
          aa_merge_order_total: taskInfo?.orderTotalCount,
          aa_merge_order_order_success_count: taskInfo?.groupCount,
          aa_task_type: taskType,
        })
      }
    },
    [ACTION_MAP, t, handleGoTaskCenter, renderMessage, handleDownload, mergerOrderResultTrack, mergeOrderTrack]
  )

  const notifyCountTemp = notifyCount
  const unReadMsgCountTemp = unReadMsgCount
  /** 更新异步消息, 通知到对应业务组件 */
  const updateAsyncTask = useCallback(
    (res: any) => {
      const resultData = jsonParse<Record<string, any>, string>(res.data)
      if (resultData.messageTitle == 'MESSAGE') {
        //这个是公告信息
        store.dispatch(setNotifyCount({ notifyCount: notifyCountTemp + 1 }))
      } else if (resultData.messageTitle == 'USER_MESSAGE') {
        //这个是站内信息
        store.dispatch(setUnReadMessageCount({ unReadCount: unReadMsgCountTemp + 1 }))
      } else if (resultData.messageTitle == 'OW_TASK') {
        //这个是任务
        const { taskId, unReadCount } = resultData
        queryUnRead(taskId)
        store.dispatch(setUnReadTaskCount({ taskCount: unReadCount }))
      } else {
      }
    },
    [queryUnRead, unReadMsgCountTemp, notifyCountTemp]
  )

  // 异步任务消息
  useEffect(() => {
    return socket.onAsyncResult(updateAsyncTask)
  }, [updateAsyncTask])

  useEffect(() => {
    // 这里要从cookie取，避免同时登录两个号错乱的情况
    const udbSubAppId = Cookies.get('osudb_subappid')
    const uid = Cookies.get('osudb_uid')

    if (uid && udbSubAppId && !isInit.current) {
      isInit.current = true
      const WEBSOCKET_UDB_BOARDCASTS = process.env.WEBSOCKET_UDB_BOARDCASTS
      const APP_ENV = process.env.APP_ENV
      // 配置
      socket.configure(() => ({
        env: APP_ENV.toUpperCase(),
        /**
         * @todo
         * 此处根据打包环境，不能根据用户真正所在地区进行就近 websocket 连接
         * 后面可以根据服务器环境确认 socket 连接地址
         */
        wsDomain: process.env.WEBSOCKET_URL,
        appId: process.env.WEBSOCKET_APP_ID,
        privateKey: process.env.WEBSOCKET_PRIVATE_KEY,
        broadcasts: WEBSOCKET_UDB_BOARDCASTS.split(','),
        udbAuthUrl: `${window.location.origin}/udb/lgn/sdk/getCookieOtp.do`,
        udbAppId: process.env.WEBSOCKET_UDB_APP_ID,
        udbSubAppId,
        uid,
      }))
      socket.open()
    }

    return () => {
      if (isInit.current) {
        socket?.close()
      }
    }
  }, [])
}
